import React from "react";
import { motion } from "framer-motion";
import { Container, Eyebrow, DisplayHeading, BodyCopy, Button } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import { color } from "@/lib/theme";
import { fadeUp, viewport } from "@/lib/motion";

// The wash behind a page hero, one per accent. Kept faint: on a white ground a
// wide blur of colour reads as a smudge rather than as light, so these sit an
// order of magnitude below what the dark scheme could carry.
const HERO_WASH = {
  brand: `radial-gradient(50% 50% at 50% 50%, ${color("brand", 0.08)}, transparent 70%)`,
  gold: `radial-gradient(50% 50% at 50% 50%, ${color("gold", 0.07)}, transparent 70%)`,
  valid: `radial-gradient(50% 50% at 50% 50%, ${color("valid", 0.07)}, transparent 70%)`,
};

export function PageHero({ eyebrow, tone = "brand", titleLines, intro, children }) {
  return (
    <section className="relative overflow-hidden pb-12 pt-32 sm:pb-16 sm:pt-36 md:pb-24 md:pt-44">
      <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-60" />
      <div
        className="pointer-events-none absolute -top-16 left-1/2 h-[260px] w-[420px] -translate-x-1/2 rounded-full blur-[90px] sm:-top-24 sm:h-[420px] sm:w-[820px] sm:blur-[120px]"
        style={{ background: HERO_WASH[tone] || HERO_WASH.brand }}
      />
      <Container className="relative">
        <motion.div variants={fadeUp} initial="hidden" animate="show">
          <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
        </motion.div>
        <DisplayHeading as="h1" className="mt-6 max-w-4xl text-[34px] leading-[1.06] sm:mt-7 sm:text-5xl md:text-6xl lg:text-[68px]">
          <MaskedLinesInView lines={titleLines} />
        </DisplayHeading>
        {intro && (
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-6 max-w-2xl sm:mt-8">
            <BodyCopy>{intro}</BodyCopy>
          </motion.div>
        )}
        {children && (
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9">
            {children}
          </motion.div>
        )}
      </Container>
    </section>
  );
}

export function CTASection({
  eyebrow = "Get started",
  title = ["Your AI already makes decisions.", "Can you prove they're governed?"],
  body = "See how Meira turns AI security, governance and assurance into evidence anyone can verify.",
}) {
  return (
    <section className="relative overflow-hidden border-t border-line-soft bg-panel-raised/40 py-20 sm:py-28 md:py-36">
      <div className="pointer-events-none absolute inset-0 tech-grid opacity-50" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[260px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/[0.035] blur-[100px] sm:h-[380px] sm:w-[760px] sm:blur-[130px]" />
      <Container className="relative text-center">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="mx-auto flex justify-center">
          <Eyebrow tone="brand">{eyebrow}</Eyebrow>
        </motion.div>
        <DisplayHeading className="mx-auto mt-6 max-w-3xl text-[28px] leading-tight sm:mt-7 sm:text-4xl md:text-5xl">
          <MaskedLinesInView lines={title} />
        </DisplayHeading>
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} custom={2}>
          <BodyCopy className="mx-auto mt-6 max-w-xl">{body}</BodyCopy>
          <div className="mx-auto mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:mt-9 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4">
            <Button to="/contact" variant="primary" data-testid="cta-book-demo" className="uppercase tracking-[0.12em]">
              Book a Demo
            </Button>
            <Button to="/verify" variant="outline" data-testid="cta-verify">
              Verify a Certificate
            </Button>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
